import { Alert } from "@mui/material";
import { TreeViewBaseItem } from "@mui/x-tree-view-pro";
import { captureError } from "@sinch/core/config/sentry";
import { RatelistType } from "@sinch/core/entities/serverEnums";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { isEmpty } from "ramda";
import React, { FC, useMemo } from "react";
import { JobsRatelists } from "@sinch/components/application/Rate/RateMigration/queries.graphql";
import {
  JobsRatelistsQuery,
  JobsRatelistsQueryVariables,
} from "@sinch/components/application/Rate/RateMigration/queries.types";
import { RateMigration } from "@sinch/components/application/Rate/RateMigration/RateMigration";
import { ExtendedTreeItemProps } from "@sinch/components/application/Rate/RateMigration/types";
import { useRateMigrationTree } from "@sinch/components/application/Rate/RateMigration/utils/tree";

export interface JobRateMigrationProps {
  sourceRatelistIds: string[];
  targetRatelistId: string;
  onChange: (arg: { jobIds: string[]; positionIds: string[] }) => void;
  type: Omit<RatelistType, RatelistType.TransportPrice | RatelistType.TransportWage>;
}

export const JobRateMigration: FC<JobRateMigrationProps> = ({
  sourceRatelistIds,
  targetRatelistId,
  onChange,
  type,
}) => {
  const { data, loading } = useQueryHandler<JobsRatelistsQuery, JobsRatelistsQueryVariables>(JobsRatelists, {
    sourceRatelistIds: sourceRatelistIds,
    ratelistId: targetRatelistId,
  });

  const { buildRateMigrationTree } = useRateMigrationTree();
  const tree: TreeViewBaseItem<ExtendedTreeItemProps>[] = useMemo(
    () =>
      buildRateMigrationTree(type === RatelistType.Price ? "priceRatelist" : "wageRatelist", data, sourceRatelistIds),
    [buildRateMigrationTree, data, sourceRatelistIds, type]
  );

  if (!loading && isEmpty(tree)) {
    captureError(new Error("Migration list after save ratelist is empty."));
    // eslint-disable-next-line i18next/no-literal-string
    return <Alert severity="warning">No items available for migration.</Alert>;
  }

  return <RateMigration loading={loading} onChange={onChange} targetRatelist={data?.ratelist} tree={tree} />;
};
